import React from 'react';
import { Briefcase, MessageCircle, Heart, Compass, Languages, Utensils, Home, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const icons = [Briefcase, MessageCircle, Heart, Compass, Languages, Utensils, Home, Sparkles];

const PERSONA_IMAGES = [
  '/images/Interview-Coach.png',
  null,
  '/images/Wellness-Begleiter.png',
  '/images/Lebens-Stratege.png',
  '/images/Sprachtutor.png',
  null,
  null,
  null,
];

const COACH_PREVIEWS = [
  { src: '/images/Interview-Coach.png', altDe: 'Interview-Coach', altEn: 'Interview Coach' },
  { src: '/images/Lebens-Stratege.png', altDe: 'Lebens-Stratege', altEn: 'Life Strategist' },
  { src: '/images/Wellness-Begleiter.png', altDe: 'Wellness-Begleiter', altEn: 'Wellness Companion' },
  { src: '/images/Sprachtutor.png', altDe: 'Sprachtutor', altEn: 'Language Tutor' },
];

const Features = () => {
  const { t, lang } = useLanguage();

  return (
    <section id="entdecken" className="py-24 relative bg-[#0a0c14]">
      <div className="absolute inset-0 opacity-40 pointer-events-none bg-[radial-gradient(ellipse_at_30%_0%,rgba(99,102,241,0.1),transparent_50%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center mb-20">
          <div>
            <span className="text-accent text-sm font-bold uppercase tracking-[0.2em]">{t.discover.kicker}</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-white font-heading leading-tight">
              {t.discover.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-accent">
                {t.discover.titleGrad}
              </span>
            </h2>
          </div>
          <div>
            <p className="text-lg text-slate-400 leading-relaxed">{t.discover.body}</p>
            <div className="mt-8 flex items-center gap-5">
              <div className="flex -space-x-4 shrink-0">
                {COACH_PREVIEWS.map((coach) => (
                  <img
                    key={coach.src}
                    src={coach.src}
                    alt={lang === 'de' ? coach.altDe : coach.altEn}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-[#0a0c14] shadow-lg"
                    width={56}
                    height={56}
                  />
                ))}
              </div>
              <div>
                <p className="text-white font-semibold">{t.discover.devicesTitle}</p>
                <p className="text-slate-500 text-sm mt-1">{t.discover.devicesSub}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <span className="text-primary-light text-sm font-bold uppercase tracking-[0.2em]">{t.features.kicker}</span>
        </div>
        <div id="funktionen" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.features.items.map((item, index) => {
            const Icon = icons[index % icons.length];
            const photo = PERSONA_IMAGES[index];
            const preview = COACH_PREVIEWS.find((c) => c.src === photo);
            const photoAlt = preview ? (lang === 'de' ? preview.altDe : preview.altEn) : item.title;

            return (
              <div
                key={item.title}
                className="group bg-card-dark border border-border-dark rounded-2xl overflow-hidden hover:border-primary/35 hover:shadow-glow/50 transition-all duration-300 hover:-translate-y-1"
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={photoAlt}
                    className="w-full h-44 object-cover object-top"
                    width={400}
                    height={176}
                  />
                ) : (
                  <div className="px-7 pt-7">
                    <div className="w-12 h-12 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary-light" />
                    </div>
                  </div>
                )}
                <div className="p-7 pt-5">
                  <h3 className="text-lg font-bold text-white mb-2 font-heading">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
